import { createClient } from '~/lib/supabase/client';
import { withImpersonation } from '~/lib/impersonation-client';
import { env } from '~/env';

export interface UserCapabilities {
  is_admin: boolean;
  is_facilitator: boolean;
  is_member: boolean;
  // #1408 — true for an approved facilitator AND for an applicant still
  // awaiting approval (hosting shell, profile edit, Stripe onboarding, draft
  // events). Publishing itself still needs a real APPROVED level.
  can_access_facilitator_mode: boolean;
}

export interface UserDetails {
  user_id: string;
  full_name: string;
  email: string;
  user_type: string;
  last_login_type?: string;
  has_facilitator_access?: boolean;
  capabilities?: UserCapabilities;
  avatar_url: string | null;
}

export type Surface = 'ADMIN' | 'FACILITATOR' | 'MEMBER';

const SURFACE_HOME: Record<Surface, string> = {
  ADMIN: '/admin',
  FACILITATOR: '/hosting',
  // /home (not /discovery) as of #391 — matches the root dispatcher,
  // logo, and Home tab.
  MEMBER: '/home',
};

/**
 * Post-login landing (unified role switching —
 * docs/plans/unified-role-switching.md): resume the last active surface if
 * the account is still entitled to it, otherwise fall back by capability
 * (admin → facilitator → member). Entitlement here only picks a landing
 * page; the API re-checks capability on every privileged call.
 */
export function resolvePostLoginSurface(
  user: Pick<UserDetails, 'last_login_type' | 'capabilities'>,
): { surface: Surface; path: string } {
  // Every environment serves the capabilities payload (#419/#420); a missing
  // object only means an unexpected response shape — treat as member-only.
  const caps: UserCapabilities = user.capabilities ?? {
    is_admin: false,
    is_facilitator: false,
    is_member: true,
    can_access_facilitator_mode: false,
  };
  const entitled: Record<Surface, boolean> = {
    ADMIN: caps.is_admin,
    // #1408 — resumes the Facilitator surface for an applicant awaiting
    // approval too, if that is the surface they last explicitly chose. The
    // fallback-by-capability order below (admin → facilitator → member) is
    // left on the stricter is_facilitator deliberately — a brand-new
    // pending applicant with no explicit choice yet still lands on Member,
    // not swept into Facilitator Mode by default.
    FACILITATOR: caps.can_access_facilitator_mode,
    MEMBER: caps.is_member,
  };

  const last = String(user.last_login_type || '').toUpperCase() as Surface;
  const surface: Surface = entitled[last]
    ? last
    : caps.is_admin
      ? 'ADMIN'
      : caps.is_facilitator
        ? 'FACILITATOR'
        : 'MEMBER';
  return { surface, path: SURFACE_HOME[surface] };
}

export async function fetchUserDetails(): Promise<UserDetails> {
  const supabase = createClient();
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${env.NEXT_PUBLIC_API_URL}/user`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${withImpersonation(token)}`,
      },
    }
  );

  if (!response.ok) {
    console.log('Failed to fetch user details', response);
    throw new Error('Failed to fetch user details');
  }

  return response.json();
}

export interface MeProfileAddress {
  type: 'HOME' | 'BILLING';
  street: string;
  building: string | null;
  city: string;
  suburb: string | null;
  state: string | null;
  postcode: string;
  country: string;
}

/** Subset of GET /user/me used to prefill forms from the member profile. */
export interface MeProfile {
  user_id: string;
  full_name: string;
  email: string;
  phone_mobile: string | null;
  company: string | null;
  gender: string | null;
  addresses: MeProfileAddress[];
}

export async function fetchMeProfile(): Promise<MeProfile> {
  const supabase = createClient();
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${env.NEXT_PUBLIC_API_URL}/user/me`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${withImpersonation(token)}`,
    },
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch member profile');
  }

  return response.json();
}

export async function fetchUserById(userId: string): Promise<UserDetails> {
  const supabase = createClient();
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${env.NEXT_PUBLIC_API_URL}/user/${userId}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${withImpersonation(token)}`,
      },
    }
  );

  if (!response.ok) {
    console.log('Failed to fetch user by ID', response);
    throw new Error('Failed to fetch user details');
  }

  return response.json();
}

export async function resetUserData(): Promise<void> {
  const supabase = createClient();
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${env.NEXT_PUBLIC_API_URL}/user/data`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${withImpersonation(token)}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to reset user data');
  }
}

export async function setLoginType(loginType: 'MEMBER' | 'FACILITATOR' | 'ADMIN'): Promise<void> {
  const supabase = createClient();
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${env.NEXT_PUBLIC_API_URL}/user/login-type`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${withImpersonation(token)}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      login_type: loginType,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to update login type');
  }
}
